// src/components/padrekko/qc/QcSummary.jsx
//
// The closer. Renders when every pad in the run is terminal (completed or
// skipped) and the operator hasn't asked to drop back into inspector mode.
// ReclamationQcTab owns that view-machine decision.
//
// Structure:
//   - Header: title with completion check, session name, started/completed
//     timestamps, X exit (mirrors QcInspector's clear-session pattern)
//   - Error banner: same shape as QcInspector — single error field, X dismiss
//   - Run rollup: 3-cell stats (pads / completed / skipped), findings-by-status
//     chips, frameworks invoked, plugging-extension-blocked count (conditional)
//   - Per-pad summary cards: status-toned, expanded by default (the summary
//     view is for scanning the whole run; no collapse). Skipped pads render
//     as a muted single-line card.
//   - Sticky footer: [Download JSON] + [View pads →]
//
// Map: this view doesn't drive map state. QcCurrentPadLayer + QcFindingsLayer
// unmount when QcInspector unmounts on the isComplete flip, leaving the map
// at whatever zoom/center it had. Accepted for v0; a v1 "show all pads on the
// map with status coloring" view would be additive layers, not a restructure.

import {
  CheckCircle2, X as XIcon, AlertCircle, Download, ChevronRight, Loader2, Printer,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useQcStore } from "../../../stores/useQcStore";
import { useAuthStore } from "../../../stores/useAuthStore";
import CaseNotesPanel from "./CaseNotesPanel";

// Per-pad-summary status tone — driven by analysis.summary.overall_status,
// which uses a different enum than per-finding status:
//   COMPLIANT     / RECLAIMED          → emerald  (good)
//   PARTIALLY_COMPLIANT                → amber    (work to do)
//   NON_COMPLIANT                      → red      (problem)
//   UNKNOWN / fallback                 → slate
const SUMMARY_STATUS_TONES = {
  COMPLIANT:           "bg-emerald-500/10 border-emerald-500/30 text-emerald-200",
  RECLAIMED:           "bg-emerald-500/10 border-emerald-500/30 text-emerald-200",
  PARTIALLY_COMPLIANT: "bg-amber-500/10   border-amber-500/30   text-amber-200",
  NON_COMPLIANT:       "bg-red-500/10     border-red-500/30     text-red-200",
};
const SUMMARY_STATUS_PILL_TONES = {
  COMPLIANT:           "bg-emerald-500/20 border-emerald-500/50 text-emerald-100",
  RECLAIMED:           "bg-emerald-500/20 border-emerald-500/50 text-emerald-100",
  PARTIALLY_COMPLIANT: "bg-amber-500/20   border-amber-500/50   text-amber-100",
  NON_COMPLIANT:       "bg-red-500/20     border-red-500/50     text-red-100",
};
const SLATE_TONE      = "bg-slate-800/30 border-slate-700/40 text-slate-400";
const SLATE_PILL_TONE = "bg-slate-700/30 border-slate-600/40 text-slate-300";

// Per-finding status tones for the rollup chips — match QcFindingCard so
// the operator's tonal memory carries over.
const FINDING_STATUS_PILL_TONES = {
  ACCEPTABLE:    "bg-emerald-500/20 border-emerald-500/50 text-emerald-100",
  VERIFY:        "bg-amber-500/20   border-amber-500/50   text-amber-100",
  NON_COMPLIANT: "bg-red-500/20     border-red-500/50     text-red-100",
};

const MICRO_PILL =
  "text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded border whitespace-nowrap";

function formatTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  } catch {
    return "";
  }
}

// Build the run-level rollup from session.pads. All derivations client-side
// — the server's /export endpoint returns the full pad data; aggregating is
// trivial enough not to need a server round-trip.
function buildRollup(pads) {
  const totals = {
    padCount: pads.length,
    completedPads: 0,
    skippedPads: 0,
    findingsByStatus: { NON_COMPLIANT: 0, VERIFY: 0, ACCEPTABLE: 0, OTHER: 0 },
    frameworks: new Set(),
    blockedPads: 0,
  };

  for (const p of pads) {
    if (p.status === "completed") totals.completedPads += 1;
    else if (p.status === "skipped") totals.skippedPads += 1;

    for (const f of p.findings ?? []) {
      const s = f.analysis?.status;
      if (s && totals.findingsByStatus[s] !== undefined) {
        totals.findingsByStatus[s] += 1;
      } else if (s) {
        totals.findingsByStatus.OTHER += 1;
      }
      const fw = f.analysis?.framework;
      if (fw) totals.frameworks.add(fw);
    }

    if (p.summary?.blocks_plugging_extension === true) {
      totals.blockedPads += 1;
    }
  }

  return totals;
}

// Small reusable cell for the 3-stat row at the top of the rollup.
function StatCell({ value, label }) {
  return (
    <div className="flex-1 rounded border border-slate-800/60 bg-slate-800/30
                    px-3 py-2 text-center">
      <div className="text-[18px] font-semibold text-slate-100 tabular-nums leading-tight">
        {value}
      </div>
      <div className="text-[9px] font-mono uppercase tracking-widest text-fg-secondary mt-0.5">
        {label}
      </div>
    </div>
  );
}

// Per-pad summary card. Skipped pads render as a compact single-line row;
// completed pads show the full Claude summary, severity, framework, findings
// count, and priority actions. No truncation — summary text in full.
function PadSummaryCard({ pad }) {
  const isSkipped = pad.status === "skipped";

  if (isSkipped) {
    return (
      <div
        data-pad-status="skipped"
        className={`qc-pad-card rounded border ${SLATE_TONE} px-2.5 py-2 flex items-center justify-between gap-2`}
      >
        <div className="flex items-center gap-1.5 min-w-0">
          <span
            data-status="SKIPPED"
            className={`qc-status-pill ${MICRO_PILL} ${SLATE_PILL_TONE}`}
          >SKIPPED</span>
          <span className="text-fg-secondary text-[9px]">·</span>
          <span className="text-[9px] font-mono uppercase tracking-widest text-fg-secondary whitespace-nowrap">
            Pad {pad.ordinal + 1}
          </span>
        </div>
        <div className="text-[11px] text-slate-400 truncate min-w-0 flex-1 text-right">
          {pad.pad_name}
        </div>
      </div>
    );
  }

  const summary = pad.summary || {};
  const status = summary.overall_status || "UNKNOWN";
  const tone = SUMMARY_STATUS_TONES[status] || SLATE_TONE;
  const pillTone = SUMMARY_STATUS_PILL_TONES[status] || SLATE_PILL_TONE;
  const findingsCount = (pad.findings || []).length;

  return (
    <div
      data-pad-status="completed"
      data-overall-status={status}
      className={`qc-pad-card rounded border ${tone} px-2.5 py-2`}
    >
      {/* Header line: status pill leads, pad ordinal + name follow */}
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <div className="flex items-center gap-1.5 min-w-0">
          <span
            data-status={status}
            className={`qc-status-pill ${MICRO_PILL} ${pillTone}`}
          >
            {status}
          </span>
          <span className="text-fg-secondary text-[9px]">·</span>
          <span className="text-[9px] font-mono uppercase tracking-widest text-fg-secondary whitespace-nowrap">
            Pad {pad.ordinal + 1}
          </span>
        </div>
      </div>

      {/* Pad name on its own line */}
      <div className="text-[11px] text-slate-100 font-medium leading-snug mb-1.5">
        {pad.pad_name}
      </div>

      {/* Meta row: severity, framework count, finding count */}
      <div className="flex items-center gap-1.5 flex-wrap mb-1.5">
        {summary.severity && summary.severity !== "None" && (
          <span className={`qc-status-pill ${MICRO_PILL} ${SLATE_PILL_TONE}`}>
            {summary.severity.toUpperCase()} SEV
          </span>
        )}
        {Array.isArray(summary.regulatory_frameworks_violated) && summary.regulatory_frameworks_violated.length > 0 && (
          <span className="text-[9px] text-slate-400">
            {summary.regulatory_frameworks_violated.join(" · ")}
          </span>
        )}
        <span className="text-[9px] text-fg-secondary">
          {findingsCount} finding{findingsCount === 1 ? "" : "s"}
        </span>
        {summary.blocks_plugging_extension === true && (
          <span className={`qc-status-pill ${MICRO_PILL} bg-red-500/15 border-red-500/40 text-red-200`}>
            Blocks plugging
          </span>
        )}
      </div>

      {/* Full summary text — no truncation. The summary view is where the
          complete record lives. */}
      {summary.summary && (
        <div className="text-[10px] text-slate-300 leading-relaxed mb-1.5">
          {summary.summary}
        </div>
      )}

      {/* Priority actions as inline chips */}
      {Array.isArray(summary.priority_actions) && summary.priority_actions.length > 0 && (
        <div className="flex flex-col gap-1 mt-1.5">
          <div className="text-[9px] font-mono uppercase tracking-widest text-fg-secondary">
            Priority actions
          </div>
          <ol className="list-decimal list-inside space-y-0.5 marker:text-slate-600">
            {summary.priority_actions.map((a, i) => (
              <li key={i} className="text-[10px] text-slate-300 leading-relaxed">
                {a}
              </li>
            ))}
          </ol>
        </div>
      )}

      {summary.estimated_timeline && summary.estimated_timeline !== "Unknown" && (
        <div className="text-[9px] text-fg-secondary italic mt-1.5">
          Timeline: {summary.estimated_timeline}
        </div>
      )}
    </div>
  );
}

// ── shared report sections (rendered in both screen + print) ──────────────

// Run rollup — 3-stat row + findings-by-status + frameworks + blocked count.
// `printMode` swaps the screen border/padding for the page-break class the
// print stylesheet keys off.
function RunRollupSection({ rollup, printMode = false }) {
  return (
    <div
      className={`px-4 py-3 space-y-3 ${
        printMode ? "qc-print-rollup-block" : "border-b border-slate-800/80"
      }`}
    >
      <div className="text-[9px] font-mono uppercase tracking-widest text-fg-secondary">
        Run rollup
      </div>

      <div className="flex items-stretch gap-1.5">
        <StatCell value={rollup.padCount}      label="Pads" />
        <StatCell value={rollup.completedPads} label="Completed" />
        <StatCell value={rollup.skippedPads}   label="Skipped" />
      </div>

      <div>
        <div className="text-[9px] font-mono uppercase tracking-widest text-fg-secondary mb-1.5">
          Findings by status
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <span
            data-status="NON_COMPLIANT"
            className={`qc-status-pill ${MICRO_PILL} ${FINDING_STATUS_PILL_TONES.NON_COMPLIANT}`}
          >
            NON_COMPLIANT · {rollup.findingsByStatus.NON_COMPLIANT}
          </span>
          <span
            data-status="VERIFY"
            className={`qc-status-pill ${MICRO_PILL} ${FINDING_STATUS_PILL_TONES.VERIFY}`}
          >
            VERIFY · {rollup.findingsByStatus.VERIFY}
          </span>
          <span
            data-status="ACCEPTABLE"
            className={`qc-status-pill ${MICRO_PILL} ${FINDING_STATUS_PILL_TONES.ACCEPTABLE}`}
          >
            ACCEPTABLE · {rollup.findingsByStatus.ACCEPTABLE}
          </span>
          {rollup.findingsByStatus.OTHER > 0 && (
            <span className={`qc-status-pill ${MICRO_PILL} ${SLATE_PILL_TONE}`}>
              OTHER · {rollup.findingsByStatus.OTHER}
            </span>
          )}
        </div>
      </div>

      {rollup.frameworks.size > 0 && (
        <div>
          <div className="text-[9px] font-mono uppercase tracking-widest text-fg-secondary mb-1.5">
            Frameworks invoked
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            {[...rollup.frameworks].sort().map((fw) => (
              <span key={fw} className={`${MICRO_PILL} ${SLATE_PILL_TONE}`}>
                {fw}
              </span>
            ))}
          </div>
        </div>
      )}

      {rollup.blockedPads > 0 && (
        <div className="text-[10px] text-red-300 leading-snug">
          <span className="font-medium">{rollup.blockedPads}</span>
          {" "}
          pad{rollup.blockedPads === 1 ? "" : "s"} block{rollup.blockedPads === 1 ? "s" : ""} plugging extension under TNRC §91.101
        </div>
      )}
    </div>
  );
}

function PerPadSection({ pads }) {
  return (
    <div className="px-4 py-3 space-y-2">
      <div className="text-[9px] font-mono uppercase tracking-widest text-fg-secondary mb-1">
        Per-pad summary · {pads.length}
      </div>
      {pads.map((p) => (
        <PadSummaryCard key={p.id} pad={p} />
      ))}
    </div>
  );
}

// ── screen-only header ───────────────────────────────────────────────────

function ScreenHeader({ session, startedAt, completedAt }) {
  return (
    <div className="px-4 py-3 border-b border-slate-800/80">
      <div className="flex items-start justify-between gap-2 mb-1">
        <div className="min-w-0">
          <div className="text-[9px] font-semibold font-mono uppercase tracking-widest text-fg-secondary flex items-center gap-1.5">
            <span>Reclamation QC</span>
            <span className="text-slate-700">·</span>
            <span className="flex items-center gap-1 text-emerald-300/90">
              <CheckCircle2 size={11} />
              Run complete
            </span>
          </div>
          <div className="text-[12px] text-slate-100 font-medium mt-0.5 truncate">
            {session.name}
          </div>
          <div className="text-[10px] text-fg-secondary mt-0.5 tabular-nums">
            Started {startedAt}
            {completedAt && (
              <>
                <span className="text-slate-700"> · </span>
                Completed {completedAt}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── print-only title page ────────────────────────────────────────────────
//
// All inline styles — Tailwind's dark palette would otherwise need a barrage
// of overrides in the print stylesheet. Inline lets the print typography
// stand on its own.

function PrintTitleBlock({ session, startedIso, completedIso, generatedIso, operator, orgName }) {
  const fmt = (iso, withSec = false) => {
    if (!iso) return "—";
    const s = new Date(iso).toISOString();
    return withSec ? s.slice(0, 19).replace("T", " ") + " UTC"
                   : s.slice(0, 16).replace("T", " ") + " UTC";
  };
  return (
    <div className="qc-print-title-block" style={{ padding: "1in 0 0.5in 0" }}>
      <div style={{ fontSize: "11pt", letterSpacing: "0.15em", color: "#555", marginBottom: "8pt" }}>
        AI PRIME · PADREKKO
      </div>
      <div style={{ fontSize: "28pt", fontWeight: 700, lineHeight: 1.15, marginBottom: "8pt" }}>
        Reclamation QC Report
      </div>
      <div style={{ fontSize: "16pt", color: "#333", marginBottom: "36pt" }}>
        <span style={{ color: "#777", fontWeight: 600, marginRight: "6pt" }}>RUN:</span>
        {session?.name || "Session"}
      </div>
      <div style={{ fontSize: "13pt", lineHeight: 1.7 }}>
        <div><strong style={{ display: "inline-block", minWidth: "100pt" }}>Started:</strong> {fmt(startedIso)}</div>
        <div><strong style={{ display: "inline-block", minWidth: "100pt" }}>Completed:</strong> {fmt(completedIso)}</div>
        {operator && (
          <div><strong style={{ display: "inline-block", minWidth: "100pt" }}>Operator:</strong> {operator}</div>
        )}
        {orgName && (
          <div><strong style={{ display: "inline-block", minWidth: "100pt" }}>Organization:</strong> {orgName}</div>
        )}
        <div style={{ marginTop: "16pt", color: "#555" }}>
          <strong style={{ display: "inline-block", minWidth: "100pt" }}>Generated:</strong> {fmt(generatedIso, true)}
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────

export default function QcSummary({ onViewPads }) {
  const session         = useQcStore((s) => s.session);
  const caseInfo        = useQcStore((s) => s.caseInfo);
  const error           = useQcStore((s) => s.error);
  const clearError      = useQcStore((s) => s.clearError);
  const exportInflight  = useQcStore((s) => s.exportInflight);
  const exportSession   = useQcStore((s) => s.exportSession);

  const user    = useAuthStore((s) => s.user);
  const orgName = useAuthStore((s) => s.orgName);

  // useMemo deps on session?.pads directly (stable ref) — `pads` below is
  // a local convenience for JSX and not used as a dep.
  const rollup = useMemo(
    () => buildRollup(session?.pads ?? []),
    [session?.pads]
  );
  const pads = session?.pads ?? [];

  // ── print state + handlers ──────────────────────────────────────────────
  const [printing, setPrinting] = useState(false);
  const [generatedIso, setGeneratedIso] = useState(null);
  const prevTitleRef = useRef(null);
  const hadRunningTitleRef = useRef(false);

  const operator =
    user?.name
    ?? user?.preferred_username
    ?? user?.email
    ?? null;

  function startPrint() {
    if (printing || !session) return;

    // Sanitize session name for filename + running title. Strip double
    // quotes specifically because the running title becomes a CSS
    // content value (a quoted string) — embedded quotes break parsing.
    const rawName = session.name || "session";
    const safeName = rawName.replace(/[^A-Za-z0-9._-]+/g, "_").slice(0, 60);
    const safeRunning = rawName.replace(/"/g, "").slice(0, 80);
    const ymd = new Date().toISOString().slice(0, 10);

    // Default download filename for "Save as PDF" — set via document.title.
    prevTitleRef.current = document.title;
    document.title = `Reclamation QC Report - ${safeName} - ${ymd}`;

    // CSS custom property for @page @bottom-left running title. Value
    // includes the literal quotes the CSS `content` declaration expects.
    const root = document.documentElement;
    hadRunningTitleRef.current = !!root.style.getPropertyValue("--qc-print-running-title");
    root.style.setProperty(
      "--qc-print-running-title",
      `"Reclamation QC · ${safeRunning}"`
    );

    // Stamp "Generated" time once at print initiation. Stable across the
    // portal's renders so the title page doesn't tick mid-print.
    setGeneratedIso(new Date().toISOString());
    setPrinting(true);
  }

  // Once `printing` flips true and the portal has mounted, kick off the
  // browser print dialog on the next frame. Restore the original doc
  // title + cleanup the CSS variable on afterprint (also fires on cancel).
  useEffect(() => {
    if (!printing) return;

    const cleanup = () => {
      setPrinting(false);
      setGeneratedIso(null);
      if (prevTitleRef.current !== null) {
        document.title = prevTitleRef.current;
        prevTitleRef.current = null;
      }
      if (!hadRunningTitleRef.current) {
        document.documentElement.style.removeProperty("--qc-print-running-title");
      }
    };

    window.addEventListener("afterprint", cleanup);
    const rafId = requestAnimationFrame(() => {
      window.print();
    });
    // Fallback: if the browser doesn't fire afterprint (rare on older
    // engines), cleanup after 60s so state doesn't strand.
    const fallback = setTimeout(cleanup, 60_000);

    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(fallback);
      window.removeEventListener("afterprint", cleanup);
    };
  }, [printing]);

  if (!session) {
    return (
      <div className="p-4 text-[11px] text-fg-secondary">
        No active session.
      </div>
    );
  }

  const startedAt   = formatTime(session.started_at);
  const completedAt = formatTime(session.completed_at) || formatTime(new Date().toISOString());

  return (
    <>
      <div className="flex flex-col">

        {/* Sticky context header — mirrors QcInspector's sticky pattern.
            Pins run + case context to the top of PadRekkoPanel's scroll
            body so the operator keeps reference while scrolling the
            rollup + per-pad cards. z-20 sits above scroll content; the
            existing sticky-bottom footer (z-10) anchors to bottom and
            never shares vertical space with this. Soft downward shadow
            signals "more content below". Print portal is independent
            (mounts under document.body via createPortal), so this
            sticky behavior does not affect the printed report. */}
        <div className="sticky top-0 z-20 bg-slate-900 shadow-[0_4px_8px_-6px_rgba(0,0,0,0.6)]">
          <ScreenHeader
            session={session}
            startedAt={startedAt}
            completedAt={completedAt}
          />
        </div>

        {/* Error banner — same pattern + tones as QcInspector. */}
        {error && (
          <div className="px-4 py-2 border-b border-slate-800/80">
            <div className="flex items-start gap-2 text-[10px] rounded
                            border border-red-500/60 bg-red-500/15
                            text-red-100 px-2.5 py-1.5 leading-relaxed">
              <AlertCircle size={11} className="mt-[1px] shrink-0" />
              <div className="flex-1">{error}</div>
              <button
                type="button"
                onClick={clearError}
                className="text-red-200/80 hover:text-red-50 transition shrink-0"
                title="Dismiss"
              >
                <XIcon size={14} />
              </button>
            </div>
          </div>
        )}

        <RunRollupSection rollup={rollup} />
        {/* Day 8 Phase A — case-level notes panel. Mounted between rollup
            and per-pad section (Flag A1). Excluded from the print portal
            below per Flag B2; the print rollup + per-pad blocks stay
            byte-identical to pre-Day-8 output. */}
        <CaseNotesPanel caseId={caseInfo?.id ?? null} />
        <PerPadSection    pads={pads} />

        {/* Sticky footer — Print + Download + View pads */}
        <div className="sticky bottom-0 z-10 bg-slate-900 border-t border-slate-800/80
                        px-4 py-3 flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => exportSession()}
              disabled={exportInflight || printing}
              className={`flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded border transition
                          ${(exportInflight || printing)
                            ? "bg-[#16f2d4]/5 border-[#16f2d4]/20 text-[#16f2d4]/40 cursor-not-allowed"
                            : "bg-[#16f2d4]/15 border-[#16f2d4]/50 text-[#16f2d4] hover:bg-[#16f2d4]/25"}`}
              title="Download the full session report as JSON"
            >
              {exportInflight
                ? <Loader2 size={11} className="animate-spin" />
                : <Download size={11} />}
              {exportInflight ? "Exporting…" : "Download JSON"}
            </button>

            <button
              type="button"
              onClick={startPrint}
              disabled={printing || exportInflight}
              className={`flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded border transition
                          ${(printing || exportInflight)
                            ? "bg-slate-800/20 border-slate-800/60 text-slate-600 cursor-not-allowed"
                            : "bg-slate-800/40 border-slate-700/60 text-slate-300 hover:bg-slate-800 hover:border-slate-600"}`}
              title="Open the browser print dialog — save as PDF or print directly"
            >
              {printing
                ? <Loader2 size={11} className="animate-spin" />
                : <Printer size={11} />}
              {printing ? "Preparing…" : "Print report"}
            </button>
          </div>

          <button
            type="button"
            onClick={onViewPads}
            className="flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded
                       border bg-slate-800/40 border-slate-700/60 text-slate-300
                       hover:bg-slate-800 hover:border-slate-600 transition"
            title="Drop back into the inspector to revisit individual pads"
          >
            View pads
            <ChevronRight size={11} />
          </button>
        </div>
      </div>

      {/* Print portal — mounts only while printing. The print stylesheet
          hides every other body child, so this is the sole content that
          renders to paper. Same RunRollupSection + PerPadSection used in
          the screen view; only the header differs (title page vs. screen
          header). Render-twice is intentional: the portal lifecycle is
          contained to print initiation only. */}
      {printing && createPortal(
        <div id="qc-print-host">
          <PrintTitleBlock
            session={session}
            startedIso={session.started_at}
            completedIso={session.completed_at ?? generatedIso}
            generatedIso={generatedIso}
            operator={operator}
            orgName={orgName}
          />
          <RunRollupSection rollup={rollup} printMode />
          <PerPadSection    pads={pads} />
        </div>,
        document.body
      )}
    </>
  );
}
